/**
 * FIFINE MX - Carrito lateral
 * ---------------------------------------------------------------------------
 * Adaptacion del mini cart de Dar Media al tema Horizon.
 *
 * QUE CAMBIO RESPECTO AL ORIGINAL Y POR QUE
 *
 * El original funcionaba por toma hostil: reescribia window.fetch y
 * XMLHttpRequest.prototype.send, sobreescribia los metodos del carrito nativo
 * y usaba un MutationObserver para cerrarlo a la fuerza. Eso peleaba con
 * cart-drawer.js, product-form.js, fly-to-cart.js y sticky-add-to-cart.js de
 * Horizon, y se rompe en cuanto Shopify actualiza el tema.
 *
 * Aqui no se parchea nada. El ajuste `cart_type` del tema esta en `page`, asi
 * que Horizon NO renderiza su drawer (snippets/cart-drawer.liquid:11) y no hay
 * con quien competir.
 *
 * EVENTOS QUE EMITE (los usa fifine-upsell.js)
 *
 *   fifine:cart:added      { productIds, cart }  productos nuevos en el carrito
 *   fifine:cart:beforeopen { productIds, cart }  cancelable: si alguien llama
 *                                                preventDefault(), el cajon no
 *                                                se abre solo. Asi el upsell
 *                                                puede mostrarse primero sin
 *                                                que se apilen dos capas.
 */

const FALLBACK_EVENTS = ['cart:lines-update', 'cart:update', 'cart:refresh'];

let CART_EVENTS = [...FALLBACK_EVENTS];
/** @type {any} Clase de evento del tema, para avisar cambios propios. */
let CartLinesUpdateEvent = null;

try {
  const mod = await import('@shopify/events');
  const name = mod?.StandardEvents?.cartLinesUpdate;
  if (name) CART_EVENTS = [...new Set([name, ...FALLBACK_EVENTS])];
  CartLinesUpdateEvent = mod?.CartLinesUpdateEvent ?? null;
} catch {
  /* Se usan los nombres de respaldo. */
}

const root = document.getElementById('fif-cart');
if (root) {
  const cfg = {
    money: root.dataset.moneyFormat || '${{amount}}',
    autoOpen: root.dataset.autoOpen === 'true',
  };

  const $ = (id) => root.querySelector(`#fif-cart-${id}`);
  const panel = $('panel');
  const overlay = $('overlay');
  const itemsEl = $('items');
  const emptyEl = $('empty');
  const footerEl = $('footer');
  const countEl = $('count');
  const subtotalEl = $('subtotal');
  const savedEl = $('saved');
  const savedTextEl = $('saved-text');
  const crossEl = $('cross');

  let lastFocus = null;
  let busy = false;
  let selfUpdate = false; // ignora el evento que emite este mismo archivo
  let known = null; // ids en el carrito tras el ultimo render; null = sin inicializar

  /* ── Utilidades ──────────────────────────────────────────────────────── */

  /** Formatea centavos con el money_format de la tienda, no con un `$` fijo. */
  const money = (cents) => {
    const amount = (cents / 100).toFixed(2);
    const [int, dec] = amount.split('.');
    const comma = int.replace(/\B(?=(\d{3})+(?!\d))/g, ',');
    const dot = int.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
    return cfg.money
      .replace(/\{\{\s*amount\s*\}\}/g, `${comma}.${dec}`)
      .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, comma)
      .replace(/\{\{\s*amount_with_comma_separator\s*\}\}/g, `${dot},${dec}`)
      .replace(/\{\{\s*amount_no_decimals_with_comma_separator\s*\}\}/g, dot);
  };

  const esc = (s) => {
    const d = document.createElement('div');
    d.appendChild(document.createTextNode(s == null ? '' : String(s)));
    return d.innerHTML;
  };

  /** Reescala la miniatura en el CDN de Shopify en vez de bajar la original. */
  const thumb = (url, px) => (url ? url.replace(/(\.\w+)(\?|$)/, `_${px}x${px}$1$2`) : '');

  const routes = () => window.Shopify?.routes?.root || '/';

  /* ── Apertura y cierre ───────────────────────────────────────────────── */

  const focusables = () =>
    [...panel.querySelectorAll('a[href], button:not([disabled]), summary, [tabindex]:not([tabindex="-1"])')].filter(
      (el) => el.offsetParent !== null
    );

  function open() {
    if (root.classList.contains('is-open')) return;
    lastFocus = document.activeElement;
    root.hidden = false;
    void panel.offsetHeight; // reflow: sin esto la transicion no arranca
    root.classList.add('is-open');
    panel.setAttribute('aria-hidden', 'false');
    document.body.classList.add('fif-cart-open');
    focusables()[0]?.focus();
  }

  function close() {
    if (!root.classList.contains('is-open')) return;
    root.classList.remove('is-open');
    panel.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('fif-cart-open');
    const done = () => {
      root.hidden = true;
      panel.removeEventListener('transitionend', done);
    };
    panel.addEventListener('transitionend', done);
    lastFocus?.focus?.();
  }

  /* ── Render ──────────────────────────────────────────────────────────── */

  function render(cart) {
    countEl.textContent = cart.item_count;

    if (cart.item_count === 0) {
      itemsEl.innerHTML = '';
      emptyEl.hidden = false;
      footerEl.hidden = true;
      if (crossEl) crossEl.hidden = true;
      return;
    }

    emptyEl.hidden = true;
    footerEl.hidden = false;
    subtotalEl.textContent = money(cart.total_price);

    // El ahorro sale de descuentos reales del carrito, nunca de un calculo
    // inventado: incluye los automaticos, como el combo del M9.
    let saved = cart.total_discount || 0;
    if (saved <= 0) {
      for (const it of cart.items) {
        if (it.original_line_price > it.final_line_price) {
          saved += it.original_line_price - it.final_line_price;
        }
      }
    }
    if (savedEl) {
      savedEl.hidden = saved <= 0;
      if (saved > 0) savedTextEl.textContent = `${savedTextEl.dataset.label || 'Ahorras'} ${money(saved)}`;
    }

    itemsEl.innerHTML = cart.items
      .map((it) => {
        const variant = it.variant_title && it.variant_title !== 'Default Title' ? it.variant_title : '';
        const discounted = it.original_line_price > it.final_line_price;
        const props = Object.entries(it.properties || {})
          .filter(([k, v]) => v && !k.startsWith('_'))
          .map(([k, v]) => `<span>${esc(k)}: ${esc(v)}</span>`)
          .join('');

        return `
          <li class="fif-cart__item" data-key="${esc(it.key)}">
            <a class="fif-cart__item-media" href="${esc(it.url)}" tabindex="-1" aria-hidden="true">
              ${it.image ? `<img src="${esc(thumb(it.image, 200))}" alt="" width="72" height="72" loading="lazy">` : ''}
            </a>
            <div class="fif-cart__item-body">
              <a class="fif-cart__item-title" href="${esc(it.url)}">${esc(it.product_title)}</a>
              ${variant ? `<span class="fif-cart__item-variant">${esc(variant)}</span>` : ''}
              ${props ? `<span class="fif-cart__item-props">${props}</span>` : ''}
              <div class="fif-cart__item-row">
                <div class="fif-cart__qty">
                  <button type="button" data-action="dec" data-key="${esc(it.key)}" aria-label="Quitar uno de ${esc(it.product_title)}">&minus;</button>
                  <span class="fif-cart__qty-val" aria-live="polite">${it.quantity}</span>
                  <button type="button" data-action="inc" data-key="${esc(it.key)}" aria-label="Agregar uno de ${esc(it.product_title)}">+</button>
                </div>
                <div class="fif-cart__item-price">
                  ${discounted ? `<s>${money(it.original_line_price)}</s>` : ''}
                  <b>${money(it.final_line_price)}</b>
                </div>
              </div>
              <button type="button" class="fif-cart__remove" data-action="remove" data-key="${esc(it.key)}">Eliminar</button>
            </div>
          </li>`;
      })
      .join('');

    syncCross(cart);
  }

  /**
   * Los sugeridos ya vienen pintados desde Liquid con su precio y descuento
   * reales. Aqui solo se oculta lo que ya esta en el carrito.
   */
  function syncCross(cart) {
    if (!crossEl) return;
    const inCart = new Set(cart.items.map((i) => i.product_id));
    let visible = 0;
    for (const card of crossEl.querySelectorAll('[data-product-id]')) {
      const hide = inCart.has(Number(card.dataset.productId));
      card.hidden = hide;
      if (!hide) visible += 1;
    }
    crossEl.hidden = visible === 0;
  }

  /* ── Datos ───────────────────────────────────────────────────────────── */

  /** @type {number|null} Cuantos articulos tenia el carrito la vez anterior. */
  let conocidoCuenta = null;

  /** @returns {number[]} ids de producto que no estaban antes en el carrito. */
  function diffAdded(cart) {
    const now = cart.items.map((i) => i.product_id);
    if (known === null) {
      known = new Set(now);
      return [];
    }
    const added = now.filter((id) => !known.has(id));
    known = new Set(now);
    return added;
  }

  async function refresh({ announce = false, autoOpen = false, intento = 0 } = {}) {
    let cart;
    try {
      const res = await fetch(`${routes()}cart.js`, { credentials: 'same-origin' });
      cart = await res.json();
    } catch {
      return; // se deja en pantalla lo ultimo que se pinto
    }

    const antes = known;
    const antesCuenta = conocidoCuenta;
    const productIds = diffAdded(cart);
    conocidoCuenta = cart.item_count;

    /* El tema avisa del alta ANTES de que el servidor la refleje, asi que
       /cart.js puede volver todavia sin el producto nuevo. Cuando eso pasa el
       diff sale vacio, no se emite `fifine:cart:added` y el upsell nunca se
       muestra. Se reintenta solo si el carrito de verdad no cambio, para no
       castigar el caso legitimo de volver a agregar lo mismo. */
    const sinCambio =
      antesCuenta !== null && cart.item_count === antesCuenta && productIds.length === 0;

    if (announce && sinCambio && intento < 2) {
      known = antes;
      conocidoCuenta = antesCuenta;
      render(cart);
      setTimeout(() => refresh({ announce, autoOpen, intento: intento + 1 }), 350);
      return;
    }

    render(cart);

    if (announce && productIds.length) {
      document.dispatchEvent(new CustomEvent('fifine:cart:added', { detail: { productIds, cart } }));
    }

    if (autoOpen) {
      // Cancelable: el upsell puede pedir que el cajon no se abra todavia.
      const ev = new CustomEvent('fifine:cart:beforeopen', {
        cancelable: true,
        detail: { productIds, cart },
      });
      if (document.dispatchEvent(ev)) open();
    }
  }

  async function changeQty(key, quantity) {
    if (busy) return;
    busy = true;
    root.classList.add('is-busy');
    try {
      const res = await fetch(`${routes()}cart/change.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        credentials: 'same-origin',
        body: JSON.stringify({ id: key, quantity }),
      });
      const cart = await res.json();
      known = new Set(cart.items.map((i) => i.product_id));
      render(cart);

      // Se avisa al tema para que el contador del header (cart-icon.js y
      // header-actions.js) se actualice. Se usa la clase real del tema, no un
      // CustomEvent suelto, porque esos componentes leen su forma.
      selfUpdate = true;
      if (CartLinesUpdateEvent) {
        document.dispatchEvent(
          new CartLinesUpdateEvent({
            action: quantity === 0 ? 'remove' : 'update',
            context: 'cart',
            lines: [{ merchandiseId: String(key), quantity }],
          })
        );
      } else {
        document.dispatchEvent(new CustomEvent(CART_EVENTS[0], { bubbles: true }));
      }
      // Se libera tras la cola de microtareas, ya entregado el evento.
      setTimeout(() => {
        selfUpdate = false;
      }, 0);
    } catch {
      /* silencio */
    } finally {
      busy = false;
      root.classList.remove('is-busy');
    }
  }

  /* ── Eventos ─────────────────────────────────────────────────────────── */

  itemsEl.addEventListener('click', (e) => {
    const btn = e.target.closest('[data-action]');
    if (!btn) return;
    const key = btn.dataset.key;
    const row = btn.closest('.fif-cart__item');
    const current = parseInt(row?.querySelector('.fif-cart__qty-val')?.textContent || '1', 10);
    if (btn.dataset.action === 'inc') changeQty(key, current + 1);
    else if (btn.dataset.action === 'dec') changeQty(key, Math.max(0, current - 1));
    else if (btn.dataset.action === 'remove') changeQty(key, 0);
  });

  root.addEventListener('click', (e) => {
    if (e.target.closest('[data-fif-cart-close]')) close();
  });

  overlay.addEventListener('click', close);

  document.addEventListener('keydown', (e) => {
    if (!root.classList.contains('is-open')) return;
    if (e.key === 'Escape') {
      close();
      return;
    }
    // Foco atrapado dentro del cajon mientras esta abierto.
    if (e.key === 'Tab') {
      const list = focusables();
      if (!list.length) return;
      const first = list[0];
      const last = list[list.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    }
  });

  /* Enlaces al carrito: se abre el cajon en vez de navegar. El enlace sigue
     siendo un <a href="/cart"> real, asi que con JS apagado o en ctrl+clic
     lleva a la pagina del carrito. */
  document.addEventListener('click', (e) => {
    if (e.defaultPrevented || e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
    if (e.target.closest('#fif-cart')) return;
    const link = e.target.closest('a[href$="/cart"], a[href*="/cart?"]');
    if (!link || link.hasAttribute('data-fif-cart-ignore')) return;
    e.preventDefault();
    refresh().then(open);
  });

  /* Alta al carrito: lo avisa el propio tema. */
  for (const name of CART_EVENTS) {
    document.addEventListener(name, () => {
      if (selfUpdate) return;
      refresh({ announce: true, autoOpen: cfg.autoOpen });
    });
  }

  window.fifineCart = {
    open: () => refresh().then(open),
    close,
    refresh: () => refresh(),
  };

  refresh();
}
